import { ActivityRecordType, TaskNouns } from "@sp-crm/core";

export type RecordTypeLabels = Record<ActivityRecordType, string>;

export type RecordTypeUsage =
    | { kind: "unknown" }
    | { kind: "known"; openTaskCounts: Record<ActivityRecordType, number> };

export interface RemovalConfirmation {
    title: string;
    message: string;
}

export const removedRecordTypes = (
    before: ActivityRecordType[],
    after: ActivityRecordType[],
): ActivityRecordType[] => before.filter(recordType => !after.includes(recordType));

export const joinRecordTypeLabels = (
    recordTypes: ActivityRecordType[],
    recordTypeLabels: RecordTypeLabels,
): string => {
    const labels = recordTypes.map(recordType => recordTypeLabels[recordType]);
    if (labels.length <= 1) {
        return labels.join("");
    }
    if (labels.length === 2) {
        return `${labels[0]} and ${labels[1]}`;
    }
    return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
};

export const recordTypeSummary = (
    recordTypes: ActivityRecordType[],
    availableRecordTypes: ActivityRecordType[],
    recordTypeLabels: RecordTypeLabels,
): string => {
    const applicable = availableRecordTypes.filter(recordType =>
        recordTypes.includes(recordType),
    );
    if (applicable.length === 0) {
        return "Applies to no records here";
    }
    if (applicable.length === availableRecordTypes.length) {
        return "Applies to all record types";
    }
    return `Applies to ${joinRecordTypeLabels(applicable, recordTypeLabels)}`;
};

const openTaskPhrase = (
    recordType: ActivityRecordType,
    openTaskCount: number,
    recordTypeLabels: RecordTypeLabels,
    nouns: TaskNouns,
): string =>
    `${openTaskCount} open ${openTaskCount === 1 ? nouns.singular : nouns.plural} on ${
        recordTypeLabels[recordType]
    }`;

export const removalConfirmation = (
    typeName: string,
    removed: ActivityRecordType[],
    usage: RecordTypeUsage,
    recordTypeLabels: RecordTypeLabels,
    nouns: TaskNouns,
): RemovalConfirmation | null => {
    if (removed.length === 0) {
        return null;
    }
    if (usage.kind === "unknown") {
        return {
            title: `Stop applying ${typeName} to ${joinRecordTypeLabels(
                removed,
                recordTypeLabels,
            )}?`,
            message: `We couldn't check whether this activity type is still in use. Any existing ${nouns.plural} keep it, but nobody will be able to choose it for those records again.`,
        };
    }
    const inUse = removed.filter(recordType => usage.openTaskCounts[recordType] > 0);
    if (inUse.length === 0) {
        return null;
    }
    const details = inUse
        .map(recordType =>
            openTaskPhrase(
                recordType,
                usage.openTaskCounts[recordType],
                recordTypeLabels,
                nouns,
            ),
        )
        .join(", ");
    return {
        title: `Stop applying ${typeName} to ${joinRecordTypeLabels(
            inUse,
            recordTypeLabels,
        )}?`,
        message: `This activity type is still in use: ${details}. Those ${nouns.plural} keep this type, but nobody will be able to choose it for those records again.`,
    };
};
